import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import {
  Button,
  DropdownMenu,
  Icon,
  Select,
  SvgIconNames,
} from '@toggle/design-system';
import clsx from 'clsx';
import React, { RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from '~/components/filter/Option';
import { FilterOption } from '~/components/update-filter-options/updateFilterOptions';
import { useCustomSources } from '~/react-query-hooks/alfred/useCustomSources';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { WebSearch } from '~/views/alfred/research/services/conversations-service';
import { useChat } from '~/views/alfred/research/use-chat/useChat';

import { TrustedSourcesModal } from './TrustedSourcesModal';
import styles from './WebSearchDropdown.module.scss';

interface DropdownOption {
  key: string;
  name: string;
  icon: SvgIconNames;
  description: string;
  value: {
    web_search: WebSearch;
  };
  isChecked: boolean;
}

export type WebSearchDropdownVariant = 'icon' | 'select';
export type ModalOpenFrom = 'option' | 'button' | undefined;

export interface WebSearchDropdownProps {
  contentRef?: RefObject<HTMLDivElement | null>;
  variant?: WebSearchDropdownVariant;
}

export const WebSearchDropdown = ({
  contentRef,
  variant = 'icon',
}: WebSearchDropdownProps) => {
  const { t } = useTranslation(['chat']);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modalOpenFrom, setModalOpenFrom] = useState<ModalOpenFrom>(undefined);

  const { webSearch, setWebSearch } = useChat(state => ({
    webSearch: state.webSearch,
    setWebSearch: state.setWebSearch,
  }));
  const { trackAlfredWebSearchSelected } = usePosthogEvents();

  const { data: customSourcesResponse } = useCustomSources();
  const emptyCustomSources = customSourcesResponse?.data?.sources?.length === 0;

  useEffect(() => {
    if (emptyCustomSources) {
      setWebSearch('block');
    }
  }, [customSourcesResponse]);

  const options: DropdownOption[] = [
    {
      key: 'restricted',
      name: t('chat:trustedSources.restricted.title'),
      icon: 'GlobeLocked',
      description: t('chat:trustedSources.restricted.description'),
      value: {
        web_search: 'restricted',
      },
      isChecked: webSearch === 'restricted',
    },
    {
      key: 'block',
      name: t('chat:trustedSources.block.title'),
      icon: 'GlobeOff',
      description: t('chat:trustedSources.block.description'),
      value: {
        web_search: 'block',
      },
      isChecked: webSearch === 'block',
    },
  ];

  const selectedOption = options.find(
    option => option.value.web_search === webSearch
  );

  const renderCustomFilterOptionLabel = (filterOption: FilterOption) => {
    return (
      <div className={styles.optionWrapper}>
        {filterOption.icon && <Icon iconName={filterOption.icon} size={16} />}
        <span className={styles.label}>{filterOption.name}</span>
      </div>
    );
  };

  const renderTrigger = () => {
    if (variant === 'select') {
      return (
        <div
          className={styles.selectWrapper}
          role="button"
          tabIndex={0}
          data-testid="web-search-select-trigger"
          aria-label={`${t('chat:scheduledQuerySettings.webSearch')}: ${
            selectedOption?.name ?? ''
          }`}
        >
          <Select
            isActive={dropdownOpen}
            inputProps={{
              value: selectedOption?.name ?? '',
              variant: 'outline',
              'aria-hidden': true,
              tabIndex: -1,
              'data-testid': 'web-search-dropdown-trigger',
              leftIcon: selectedOption?.icon ? (
                <Icon iconName={selectedOption.icon} size={16} />
              ) : undefined,
            }}
          />
        </div>
      );
    }

    return (
      <div>
        <Button
          className={styles.stylesButton}
          variant="empty"
          iconName={selectedOption?.icon}
          size="small"
          aria-label={selectedOption?.name}
          data-testid="web-search-dropdown-trigger"
        />
      </div>
    );
  };

  const handleOpenModal = (from: ModalOpenFrom) => {
    setDropdownOpen(false);
    setModalOpenFrom(from);
  };

  const dropdownMenu = (
    <DropdownMenu
      contentRef={contentRef}
      triggerNode={renderTrigger()}
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      className={clsx(styles.styledDropdownMenu, {
        [styles.select]: variant === 'select',
      })}
      contentProps={
        variant === 'select'
          ? {
              style: {
                width: 'var(--radix-dropdown-menu-trigger-width)',
                minWidth: 'var(--radix-dropdown-menu-trigger-width)',
                maxWidth: 'var(--radix-dropdown-menu-trigger-width)',
              },
            }
          : undefined
      }
    >
      {options.map(option => {
        return (
          <div key={option.key}>
            <Option
              option={option}
              isChecked={option.isChecked}
              indentedDescription={24}
              handleClick={() => {
                if (option.key === 'restricted' && emptyCustomSources) {
                  handleOpenModal('option');
                  return;
                }

                setWebSearch(option.value.web_search);
                const typeMap: Record<
                  WebSearch,
                  'general' | 'trusted' | 'none'
                > = {
                  open: 'general',
                  restricted: 'trusted',
                  block: 'none',
                };
                trackAlfredWebSearchSelected(
                  POSTHOG_TRACKING_SOURCES.ALFRED,
                  typeMap[option.value.web_search]
                );
              }}
              renderCustomFilterOptionLabel={renderCustomFilterOptionLabel}
            />
            {option.key === 'restricted' && (
              <RadixDropdown.Item
                asChild
                onSelect={() => handleOpenModal('button')}
              >
                <Button
                  className={styles.stylesXsButton}
                  variant="tertiary"
                  size="xsmall"
                  label={t('chat:trustedSources.viewTrustedSources')}
                />
              </RadixDropdown.Item>
            )}
          </div>
        );
      })}
    </DropdownMenu>
  );

  return (
    <>
      {dropdownMenu}
      {modalOpenFrom && (
        <TrustedSourcesModal
          isOpen={modalOpenFrom}
          onClose={() => setModalOpenFrom(undefined)}
          setWebSearch={setWebSearch}
        />
      )}
    </>
  );
};
